// Messagerie chat thread (web) — faithful reproduction of heric's FlatChatPanel
// design (colors, bubbles, grouping, composer), wired to the hexagone store.

import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { collection, query, where, orderBy, limit as fsLimit, onSnapshot } from "firebase/firestore";
import { db } from "../config/FirebaseConfig";
import useMessagerieStore from "../stores/messagerieStore";
import {
  Icon,
  MemberAvatar,
  AvatarStack,
  getMember,
  EMOJI_LIST,
  formatTime,
} from "./messagerieUi";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import { queryCollectionAlgolia } from "../models/AlgoliaFunctions";

// --- Bubble radius (heric "WhatsApp" style) ---
function getBubbleRadius(isOwn, position) {
  if (position === "solo") return isOwn ? "10px 10px 4px 10px" : "10px 10px 10px 4px";
  if (isOwn) {
    if (position === "first") return "10px 10px 4px 10px";
    if (position === "middle") return "10px 4px 4px 10px";
    return "10px 4px 10px 10px";
  }
  if (position === "first") return "10px 10px 10px 4px";
  if (position === "middle") return "4px 10px 10px 4px";
  return "4px 10px 10px 10px";
}

// Calendar-day bucket for a message (local time), used both to break message
// groups across midnight and to render day separators.
function msgDayKey(msg) {
  const iso = msg && msg.created_at;
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toDateString();
}

function groupMessages(messages, currentUserId) {
  const groups = [];
  for (const msg of messages) {
    const sid = msg.sender_id || "";
    const last = groups[groups.length - 1];
    const dayChanged = last && msgDayKey(last.messages[0]) !== msgDayKey(msg);
    if (msg.deleted || !last || last.senderId !== sid || last.messages.some((m) => m.deleted) || dayChanged) {
      groups.push({ senderId: sid, messages: [msg], isOwn: sid === currentUserId });
    } else {
      last.messages.push(msg);
    }
  }
  return groups;
}

function ReadTicks({ status }) {
  if (status === "failed") {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 2L10 10M10 2L2 10" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (status === "sent") {
    return (
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
        <path d="M1 4.5L4 7.5L10.5 1" stroke="#A3A3A3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  const color = status === "read_all" ? "#34B7F1" : "#A3A3A3";
  return (
    <svg width="17" height="9" viewBox="0 0 17 9" fill="none">
      <path d="M1 4.5L4 7.5L10.5 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 4.5L8.5 7.5L15 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Download an attachment (ported from heric's handleDownload): fetch as a blob
// so cross-origin storage URLs download instead of navigating, with a
// new-tab fallback. Falls back to the inline data URL for optimistic images.
async function handleDownload(url, base64, name) {
  const href = base64 || url;
  if (!href) return;
  try {
    const res = await fetch(href);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(href, "_blank");
  }
}

// --- Attachment helpers ---
function isImageAtt(a) {
  return !!a && (a.type === "image" || (a.mime_type || "").startsWith("image/"));
}
function isAudioAtt(a) {
  return !!a && (a.mime_type || "").startsWith("audio/");
}
function isVideoAtt(a) {
  return (
    !!a &&
    ((a.mime_type || "").startsWith("video/") ||
      /\.(mp4|mov|m4v|webm|3gp)$/i.test(a.name || ""))
  );
}
function isPdfAtt(a) {
  return (
    !!a &&
    ((a.name || "").toLowerCase().endsWith(".pdf") ||
      (a.mime_type || "") === "application/pdf")
  );
}
function getAttachments(message) {
  // Prefer the attachments[] list; fall back to the legacy single attachment.
  if (message.attachments && message.attachments.length) return message.attachments;
  return message.attachment ? [message.attachment] : [];
}
function formatSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  const mo = bytes / (1024 * 1024);
  return `${mo < 10 ? mo.toFixed(1) : Math.round(mo)} Mo`;
}

// WhatsApp-style image album: 1 image full; 2-4 in a grid; >4 shows +N.
function ImageAlbum({ images, hovered, onOpen, onDownload }) {
  const W = 240;
  if (images.length === 1) {
    const a = images[0];
    return (
      <div
        onClick={() => onOpen(a)}
        title="Agrandir"
        style={{ width: W, position: "relative", cursor: "pointer", borderRadius: 8, overflow: "hidden" }}
      >
        <img src={a.base64Data || a.url} alt={a.name || "image"} style={{ display: "block", width: "100%", height: "auto" }} />
        {hovered && (
          <div className="absolute flex flex-row" style={{ top: 6, left: 6, gap: 6 }}>
            <span className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 6, background: "rgba(0,0,0,0.55)" }}>
              <Icon name="eye" size={15} color="#FFFFFF" />
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(a); }}
              title="Télécharger"
              className="flex items-center justify-center"
              style={{ width: 28, height: 28, borderRadius: 6, background: "rgba(0,0,0,0.55)", border: "none", cursor: "pointer" }}
            >
              <Icon name="download" size={15} color="#FFFFFF" />
            </button>
          </div>
        )}
      </div>
    );
  }
  const shown = images.slice(0, 4);
  const extra = images.length - 4;
  return (
    <div style={{ width: W, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, borderRadius: 8, overflow: "hidden" }}>
      {shown.map((a, i) => {
        const wideFirst = images.length === 3 && i === 0;
        return (
          <div
            key={i}
            onClick={() => onOpen(a)}
            style={{ position: "relative", cursor: "pointer", overflow: "hidden", gridColumn: wideFirst ? "1 / span 2" : undefined, aspectRatio: wideFirst ? "2 / 1" : "1 / 1" }}
          >
            <img src={a.base64Data || a.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            {i === 3 && extra > 0 && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}>
                <span style={{ color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 600, fontSize: 22 }}>+{extra}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// WhatsApp-style PDF card: page-1 thumbnail + name + meta + Ouvrir/Enregistrer.
function PdfCard({ att, isOwn, onOpen, onDownload }) {
  const accent = isOwn ? "#FFFFFF" : "#464DFF";
  const divider = isOwn ? "rgba(255,255,255,0.16)" : "#F0F0F0";
  const meta = [
    att.pages ? `${att.pages} page${att.pages > 1 ? "s" : ""}` : null,
    "PDF",
    formatSize(att.size),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div style={{ width: 240, borderRadius: 8, overflow: "hidden", background: isOwn ? "rgba(255,255,255,0.08)" : "#FFFFFF", border: isOwn ? "none" : "1px solid #ECECEC" }}>
      {att.preview_url && (
        <div onClick={() => onOpen(att)} style={{ cursor: "pointer", background: "#E9E9E9" }}>
          <img src={att.preview_url} alt="" style={{ display: "block", width: "100%", height: 130, objectFit: "cover", objectPosition: "top" }} />
        </div>
      )}
      <div className="flex flex-row items-center" style={{ gap: 8, padding: "8px 10px" }}>
        <div className="flex items-center justify-center" style={{ width: 28, height: 32, borderRadius: 5, background: "#FF3B30", flexShrink: 0 }}>
          <span style={{ color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 700, fontSize: 8 }}>PDF</span>
        </div>
        <div className="flex flex-col" style={{ flex: 1, overflow: "hidden" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isOwn ? "#FFFFFF" : "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name || "Document.pdf"}</span>
          <span style={{ fontFamily: "Poppins", fontSize: 11, color: isOwn ? "rgba(255,255,255,0.6)" : "#A1A1A1" }}>{meta}</span>
        </div>
      </div>
      <div className="flex flex-row" style={{ borderTop: `1px solid ${divider}` }}>
        <button onClick={() => onOpen(att)} className="flex-1 hover:opacity-80" style={{ padding: 8, background: "transparent", border: "none", cursor: "pointer", fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: accent }}>Ouvrir</button>
        <div style={{ width: 1, background: divider }} />
        <button onClick={() => onDownload(att)} className="flex-1 hover:opacity-80" style={{ padding: 8, background: "transparent", border: "none", cursor: "pointer", fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: accent }}>Enregistrer</button>
      </div>
    </div>
  );
}

// Inline video: shows the first frame as a poster + a clear ▶ overlay so it
// reads as a playable video (not a black box). Click swaps to a real
// <video controls autoPlay>. Mirrors the native VideoMessage component.
function VideoBubble({ url }) {
  const [started, setStarted] = useState(false);
  if (started) {
    return (
      <video
        src={url}
        controls
        autoPlay
        preload="metadata"
        style={{ width: 240, maxHeight: 320, borderRadius: 8, background: "#000000", display: "block" }}
      />
    );
  }
  return (
    <div
      onClick={() => setStarted(true)}
      title="Lire la vidéo"
      style={{ position: "relative", width: 240, borderRadius: 8, overflow: "hidden", background: "#000000", cursor: "pointer" }}
    >
      <video src={url} muted preload="metadata" style={{ width: "100%", maxHeight: 320, display: "block" }} />
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.25)" }}>
        <div className="flex items-center justify-center" style={{ width: 54, height: 54, borderRadius: 27, background: "rgba(0,0,0,0.55)" }}>
          <span style={{ color: "#FFFFFF", fontSize: 22, marginLeft: 3, lineHeight: 1 }}>▶</span>
        </div>
      </div>
    </div>
  );
}

// Detect phone numbers / URLs / emails in plain message text (mirrors the
// mobile MessageBody). Group 1: URL, 2: email, 3: phone (FR formats like
// "06 12 34 56 78", "+33 6 12 34 56 78", "0612345678", plus generic intl).
const LINKIFY_RE = new RegExp(
  [
    "(https?:\\/\\/[^\\s]+)",
    "([\\w.+-]+@[\\w-]+\\.[\\w.-]+)",
    "((?:(?:\\+|00)33[\\s.\\-]?(?:\\(0\\)[\\s.\\-]?)?|0)[1-9](?:[\\s.\\-]?\\d{2}){4}" +
      "|(?:\\+|00)\\d{1,3}[\\s.\\-]?\\d(?:[\\s.\\-]?\\d){7,12})",
  ].join("|"),
  "g"
);

// Turn a plain text run into an array of nodes, replacing phone/URL/email
// matches with clickable links (call / open / mail). Returns the raw string
// untouched when there's nothing to linkify.
function linkify(text, isOwn) {
  if (!text) return text;
  const re = new RegExp(LINKIFY_RE); // fresh instance: no shared lastIndex
  let m = re.exec(text);
  if (!m) return text; // nothing to linkify — return the raw string untouched
  const color = isOwn ? "#FFFFFF" : "#4F46E5";
  const nodes = [];
  let last = 0;
  let key = 0;
  while (m !== null) {
    const [match, url, email, phone] = m;
    if (m.index > last) nodes.push(text.slice(last, m.index));
    let href = "";
    let external = false;
    if (url) { href = url; external = true; }
    else if (email) href = `mailto:${email}`;
    else if (phone) href = `tel:${phone.replace(/[^\d+]/g, "")}`;
    nodes.push(
      <a
        key={`lk${key++}`}
        href={href}
        onClick={(e) => e.stopPropagation()}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        style={{ color, textDecoration: "underline" }}
      >
        {match}
      </a>
    );
    last = m.index + match.length;
    m = re.exec(text);
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function RenderMessageText({ text, mentions, members, folderRefs, navigateWorkspace, isOwn }) {
  if (!text) return null;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const byLenDesc = (a, b) => b.length - a.length;

  const mentionNames = (mentions || [])
    .map((id) => getMember(members, id))
    .filter(Boolean)
    .map((m) => m.name)
    .sort(byLenDesc);

  const folders = (folderRefs || []).filter((f) => f && f.name);
  const folderByName = {};
  folders.forEach((f) => { folderByName[f.name] = f; });
  const folderNames = folders.map((f) => f.name).sort(byLenDesc);

  if (!mentionNames.length && !folderNames.length) return <>{linkify(text, isOwn)}</>;

  const tokens = [];
  if (mentionNames.length) tokens.push(`@(?:${mentionNames.map(esc).join("|")})`);
  if (folderNames.length) tokens.push(`/(?:${folderNames.map(esc).join("|")})`);
  const pattern = new RegExp(`(${tokens.join("|")})`, "g");
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        if (part && part.startsWith("@") && mentionNames.some((n) => part === `@${n}`)) {
          return (
            <span key={i} className="font-semibold" style={{ color: "#6366f1" }}>{part}</span>
          );
        }
        if (part && part.startsWith("/")) {
          const f = folderByName[part.slice(1)];
          if (f) {
            // Adapt to the bubble: light chip on the dark own-bubble, indigo
            // chip on the light other-bubble.
            const chipBg = isOwn ? "rgba(255,255,255,0.22)" : "rgba(99,102,241,0.14)";
            const chipColor = isOwn ? "#FFFFFF" : "#4F46E5";
            return (
              <span
                key={i}
                onClick={(e) => { e.stopPropagation(); navigateWorkspace && navigateWorkspace(`/folder/${f.id}`); }}
                title="Ouvrir le dossier"
                style={{ display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer", background: chipBg, color: chipColor, borderRadius: 6, padding: "0 6px", fontWeight: 600, lineHeight: "20px" }}
              >
                <Icon name="file" size={12} color={chipColor} />
                {f.name}
              </span>
            );
          }
        }
        return <React.Fragment key={i}>{linkify(part, isOwn)}</React.Fragment>;
      })}
    </>
  );
}

function ReactionPill({ emoji, userIds, isMine, onClick }) {
  return (
    <button
      onClick={onClick}
      title="Voir qui a réagi"
      className="flex items-center justify-center"
      style={{ height: 20, padding: 0, gap: 1, background: "transparent", border: "none", cursor: "pointer" }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>
      {userIds.length > 1 && (
        <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: isMine ? "#6366f1" : "#999999" }}>
          {userIds.length}
        </span>
      )}
    </button>
  );
}

// "Lundi 14:32" / "Hier à 09:12" / "12/05/2026 à 14:32"
function formatReadTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    const sameDay = d.toDateString() === now.toDateString();
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    const isYest = d.toDateString() === yest.toDateString();
    if (sameDay) return `Aujourd'hui à ${time}`;
    if (isYest) return `Hier à ${time}`;
    return `${d.toLocaleDateString("fr-FR")} à ${time}`;
  } catch {
    return "";
  }
}

// Resolve when `receipt`'s user FIRST saw the message at index `msgIdx`.
// Checkpoints are appended as the read pointer advances; the earliest checkpoint
// (by time) whose message sits at-or-after this message is when it was seen.
// Falls back to the single last_read_at for legacy receipts with no checkpoints.
function readTimeForMessage(receipt, msgIdx, allMessages) {
  if (!receipt || msgIdx < 0) return receipt ? receipt.last_read_at || null : null;
  let best = null;
  for (const cp of receipt.checkpoints || []) {
    const ci = (allMessages || []).findIndex((m) => m.id === cp.message_id);
    if (ci >= 0 && ci >= msgIdx && (best === null || (cp.read_at || "") < best)) {
      best = cp.read_at || "";
    }
  }
  return best !== null ? best : receipt.last_read_at || null;
}

// "Aujourd'hui" / "Hier" / "22/05/2026"
function formatDaySeparator(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return "Aujourd'hui";
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "Hier";
    return d.toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
}

function DaySeparator({ iso }) {
  const label = formatDaySeparator(iso);
  if (!label) return null;
  return (
    <div className="flex items-center justify-center" style={{ margin: "2px 0" }}>
      <span
        style={{
          fontFamily: "Poppins",
          fontWeight: 500,
          fontSize: 12,
          color: "#525252",
          background: "#FFFFFF",
          border: "1px solid #ECECEC",
          borderRadius: 999,
          padding: "4px 12px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {label}
      </span>
    </div>
  );
}

function InfoRow({ member, right }) {
  return (
    <div className="flex flex-row items-center" style={{ gap: 10, padding: "6px 0" }}>
      <MemberAvatar member={member} size={32} />
      <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#1E1E1E", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {member.name}
      </span>
      {right}
    </div>
  );
}

// WhatsApp-style "Infos du message": who reacted (and with what) + who has seen
// the message and when (derived from per-user last_read_message_id/last_read_at).
function MessageInfoModal({ message, members, readReceipts, allMessages, discussionMembers, currentUserId, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const reactions = message.reactions || [];

  const msgIdx = (allMessages || []).findIndex((m) => m.id === message.id);
  const recipientIds = (discussionMembers || []).filter((id) => id !== message.sender_id);
  const readBy = [];
  const pending = [];
  for (const uid of recipientIds) {
    const r = (readReceipts || []).find((x) => x.user_id === uid);
    let hasRead = false;
    let at = null;
    if (r && r.last_read_message_id) {
      const ri = (allMessages || []).findIndex((m) => m.id === r.last_read_message_id);
      if (ri >= 0 && msgIdx >= 0 && ri >= msgIdx) { hasRead = true; at = readTimeForMessage(r, msgIdx, allMessages); }
    }
    const member = getMember(members, uid) || { id: uid, name: "Inconnu" };
    if (hasRead) readBy.push({ member, at });
    else pending.push({ member });
  }
  readBy.sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  const preview = message.deleted
    ? "Message supprimé"
    : message.text || (message.attachment ? (message.attachment.name || "Pièce jointe") : "");

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 380, maxWidth: "92vw", maxHeight: "82vh", borderRadius: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between border-b border-neutral-200 flex-shrink-0" style={{ padding: "12px 16px" }}>
          <span className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 15 }}>Infos du message</span>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg hover:bg-neutral-100" style={{ width: 30, height: 30 }}>
            <Icon name="close" size={16} color="#525252" />
          </button>
        </div>

        <div className="flex flex-col overflow-y-auto" style={{ padding: "12px 16px", gap: 16 }}>
          {/* Message preview */}
          {preview ? (
            <div style={{ background: "#F5F5F5", borderRadius: 10, padding: "8px 12px" }}>
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#525252", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {preview}
              </span>
            </div>
          ) : null}

          {/* Reactions */}
          {reactions.length > 0 && (
            <div className="flex flex-col" style={{ gap: 2 }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                Réactions ({reactions.length})
              </span>
              {reactions
                .slice()
                .sort((a, b) => (a.emoji || "").localeCompare(b.emoji || ""))
                .map((r, i) => {
                  const member = getMember(members, r.user_id) || { id: r.user_id, name: "Inconnu" };
                  return (
                    <InfoRow
                      key={`${r.user_id}-${r.emoji}-${i}`}
                      member={member}
                      right={<span style={{ fontSize: 18, lineHeight: 1 }}>{r.emoji}</span>}
                    />
                  );
                })}
            </div>
          )}

          {/* Read by */}
          <div className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#34B7F1", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
              Vu par ({readBy.length})
            </span>
            {readBy.length ? (
              readBy.map(({ member, at }) => (
                <InfoRow
                  key={member.id}
                  member={member}
                  right={<span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1", flexShrink: 0 }}>{formatReadTime(at)}</span>}
                />
              ))
            ) : (
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", padding: "2px 0" }}>Personne pour l'instant</span>
            )}
          </div>

          {/* Pending */}
          {pending.length > 0 && (
            <div className="flex flex-col" style={{ gap: 2 }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                En attente ({pending.length})
              </span>
              {pending.map(({ member }) => (
                <InfoRow
                  key={member.id}
                  member={member}
                  right={<span style={{ fontFamily: "Poppins", fontSize: 12, color: "#C4C4C4", flexShrink: 0 }}>Non vu</span>}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Forward a message to another discussion.
function ForwardModal({ currentDiscussionId, onForward, onClose }) {
  // Subscribe here (only while the modal is open) instead of in every
  // MessageBubble — otherwise every bubble re-renders whenever the discussions
  // list changes (incoming message anywhere, reaction, read receipt…).
  const discussions = useMessagerieStore((s) => s.discussions);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const list = (discussions || [])
    .filter((d) => d.id !== currentDiscussionId)
    .filter((d) => (d.name || "").toLowerCase().includes(q.toLowerCase()));

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 380, maxWidth: "92vw", maxHeight: "82vh", borderRadius: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row items-center justify-between border-b border-neutral-200 flex-shrink-0" style={{ padding: "12px 16px" }}>
          <span className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 15 }}>Transférer à…</span>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg hover:bg-neutral-100" style={{ width: 30, height: 30 }}>
            <Icon name="close" size={16} color="#525252" />
          </button>
        </div>
        <div style={{ padding: "10px 16px" }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une conversation…"
            className="w-full outline-none"
            style={{ fontFamily: "Poppins", fontSize: 13, background: "#F5F5F5", borderRadius: 8, padding: "8px 12px" }}
          />
        </div>
        <div className="flex flex-col overflow-y-auto" style={{ padding: "0 8px 12px" }}>
          {list.length ? (
            list.map((d) => (
              <button
                key={d.id}
                disabled={sending != null}
                onClick={async () => {
                  setSending(d.id);
                  try { await onForward(d.id); } finally { setSending(null); onClose(); }
                }}
                className="flex flex-row items-center hover:bg-neutral-50 w-full"
                style={{ gap: 10, padding: "9px 8px", borderRadius: 8, opacity: sending && sending !== d.id ? 0.5 : 1 }}
              >
                <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#1E1E1E", flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.name || "Conversation"}
                </span>
                {sending === d.id && <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Envoi…</span>}
              </button>
            ))
          ) : (
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", padding: "8px" }}>Aucune conversation</span>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// --- Poll (sondage) card ---
// Rendered inside a message bubble when message.poll is set. Clicking an option
// toggles the current user's vote (single- or multi-choice); results show as
// fill bars with live percentages, vote counts and (unless anonymous) voters.
function PollCard({ message, isOwn, currentUserId, members, onVote, onClose }) {
  const poll = message.poll || {};
  const options = poll.options || [];
  const closed = !!poll.closed;
  const multi = !!poll.multi;
  const anonymous = !!poll.anonymous;
  const totalVotes = options.reduce((s, o) => s + (o.votes || []).length, 0);
  const totalVoters = new Set(options.flatMap((o) => o.votes || [])).size;
  const myVotes = new Set(
    options.filter((o) => (o.votes || []).includes(currentUserId)).map((o) => o.id)
  );
  const canClose =
    !closed && (poll.created_by === currentUserId || message.sender_id === currentUserId);

  const textColor = isOwn ? "#FFFFFF" : "#1E1E1E";
  const subColor = isOwn ? "rgba(255,255,255,0.6)" : "#A1A1A1";
  const fillBg = isOwn ? "rgba(255,255,255,0.28)" : "rgba(99,102,241,0.16)";
  const accent = isOwn ? "#FFFFFF" : "#6366f1";

  // Per-option toggle: just tell the backend which option was tapped; it flips
  // the vote (multi) or moves it / clears it (single choice).
  const toggle = (optId) => {
    if (closed) return;
    onVote(optId);
  };

  return (
    <div style={{ minWidth: 244, maxWidth: 300, display: "flex", flexDirection: "column", gap: 8 }}>
      <div className="flex flex-row items-center" style={{ gap: 6 }}>
        <Icon name="poll" size={14} color={accent} />
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: textColor, lineHeight: "18px" }}>
          {poll.question}
        </span>
      </div>
      <span style={{ fontFamily: "Poppins", fontSize: 11, color: subColor }}>
        {multi ? "Choix multiple" : "Choix unique"}
        {closed ? " · Terminé" : ""}
      </span>

      <div className="flex flex-col" style={{ gap: 6 }}>
        {options.map((o) => {
          const votes = o.votes || [];
          const pct = totalVotes ? Math.round((votes.length / totalVotes) * 100) : 0;
          const mine = myVotes.has(o.id);
          const voters = anonymous
            ? []
            : votes.map((uid) => getMember(members, uid)).filter(Boolean);
          return (
            <button
              key={o.id}
              onClick={() => toggle(o.id)}
              disabled={closed}
              className="relative w-full"
              style={{
                borderRadius: 8,
                overflow: "hidden",
                border: `1px solid ${mine ? accent : isOwn ? "rgba(255,255,255,0.2)" : "#ECECEC"}`,
                background: "transparent",
                cursor: closed ? "default" : "pointer",
                padding: 0,
                textAlign: "left",
              }}
            >
              <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${pct}%`, background: fillBg, transition: "width 0.3s ease" }} />
              <div className="relative flex flex-row items-center" style={{ gap: 8, padding: "7px 10px" }}>
                <span
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 16, height: 16, borderRadius: multi ? 4 : 8, border: `1.5px solid ${mine ? accent : subColor}`, background: mine ? accent : "transparent" }}
                >
                  {mine && <Icon name="check" size={10} color={isOwn ? "#000000" : "#FFFFFF"} />}
                </span>
                <span style={{ flex: 1, fontFamily: "Poppins", fontWeight: mine ? 600 : 500, fontSize: 13, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {o.text}
                </span>
                {voters.length > 0 && (
                  <span className="flex-shrink-0">
                    <AvatarStack members={voters} size={18} maxVisible={3} />
                  </span>
                )}
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: textColor, flexShrink: 0, minWidth: 30, textAlign: "right" }}>
                  {pct}%
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-row items-center justify-between">
        <span style={{ fontFamily: "Poppins", fontSize: 11, color: subColor }}>
          {totalVoters} vote{totalVoters > 1 ? "s" : ""}
        </span>
        {canClose && (
          <button
            onClick={onClose}
            className="hover:underline"
            style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: accent, background: "none", border: "none", cursor: "pointer" }}
          >
            Clôturer
          </button>
        )}
      </div>
    </div>
  );
}

// --- Single bubble ---
const MessageBubble = React.memo(function MessageBubble({ message, position, showTimestamp, readStatus, isOwn, members, discussionId, readReceipts, allMessages, discussionMembers, navigateWorkspace, onJump, highlight, isPinned, currentUserId: currentUserIdProp }) {
  const toggleReaction = useMessagerieStore((s) => s.toggleReaction);
  const pinMessage = useMessagerieStore((s) => s.pinMessage);
  const unpinMessage = useMessagerieStore((s) => s.unpinMessage);
  const deleteMessage = useMessagerieStore((s) => s.deleteMessage);
  const editMessage = useMessagerieStore((s) => s.editMessage);
  // Prefer the id threaded down from the screen (same one that drives isOwn /
  // read ticks) — the store's userId can fall back to the Firebase uid if the
  // hexagone user doc wasn't loaded when the global listener first installed,
  // which would never match the hexagone user_id stored on votes/reactions.
  const storeUserId = useMessagerieStore((s) => s.userId);
  const currentUserId = currentUserIdProp || storeUserId;
  const setReplyTarget = useMessagerieStore((s) => s.setReplyTarget);
  const sendMessage = useMessagerieStore((s) => s.sendMessage);
  const votePoll = useMessagerieStore((s) => s.votePoll);
  const closePoll = useMessagerieStore((s) => s.closePoll);

  const [hovered, setHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  // Lightbox holds a gallery (list of attachments) + the active index so the
  // user can page between sibling images of the same message.
  const [lightbox, setLightbox] = useState(null);
  const lightboxAtt = lightbox ? lightbox.list[lightbox.idx] : null;
  const openLightbox = (list, att) => setLightbox({ list, idx: Math.max(0, list.indexOf(att)) });
  const lightboxStep = (delta) =>
    setLightbox((lb) => (lb ? { ...lb, idx: (lb.idx + delta + lb.list.length) % lb.list.length } : lb));
  const [infoOpen, setInfoOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!showDropdown) { setDropdownPos(null); return; }
    function onDoc(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) setShowDropdown(false);
    }
    document.addEventListener("mousedown", onDoc);
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      requestAnimationFrame(() => {
        const w = dropdownRef.current?.offsetWidth || 200;
        const h = dropdownRef.current?.offsetHeight || 200;
        const top = rect.bottom + 4 + h > window.innerHeight ? rect.top - h - 4 : rect.bottom + 4;
        const left = window.innerWidth - rect.left >= w ? rect.left : rect.right - w;
        setDropdownPos({ top, left: Math.max(4, left) });
      });
    }
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showDropdown]);

  const bubbleBg = isOwn ? "#000000" : "#F5F5F5";
  const bubbleRadius = getBubbleRadius(isOwn, position);

  const grouped = (message.reactions || []).reduce((acc, r) => {
    const uid = r.user_id || "";
    (acc[r.emoji] = acc[r.emoji] || []).push(uid);
    return acc;
  }, {});
  const hasReactions = Object.keys(grouped).length > 0;

  const canEdit = isOwn && !message.deleted && !!message.created_at &&
    Date.now() - new Date(message.created_at).getTime() < 60 * 60 * 1000;

  const attachments = getAttachments(message);
  const imageAtts = attachments.filter(isImageAtt);
  const audioAtts = attachments.filter(isAudioAtt);
  const videoAtts = attachments.filter((a) => !isImageAtt(a) && !isAudioAtt(a) && isVideoAtt(a));
  const docAtts = attachments.filter((a) => !isImageAtt(a) && !isAudioAtt(a) && !isVideoAtt(a));

  if (message.deleted) {
    return (
      <div
        id={`msg-${message.id}`}
        style={{ padding: "8px 12px", background: bubbleBg, borderRadius: bubbleRadius, maxWidth: "65%", opacity: 0.7, scrollMarginTop: 80, scrollMarginBottom: 80, transition: "box-shadow 0.3s ease", boxShadow: highlight ? "0 0 0 3px rgba(99,102,241,0.7)" : "none" }}
      >
        <span className="italic" style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>
          Ce message a été supprimé
        </span>
      </div>
    );
  }

  const saveEdit = () => {
    const t = editText.trim();
    if (t && t !== message.text) editMessage(discussionId, message.id, t);
    setIsEditing(false);
  };

  return (
    <div
      id={`msg-${message.id}`}
      className="relative"
      style={{ minWidth: 80, width: "fit-content", paddingBottom: hasReactions ? 14 : 0, borderRadius: 12, scrollMarginTop: 80, scrollMarginBottom: 80, transition: "box-shadow 0.3s ease", boxShadow: highlight ? "0 0 0 3px rgba(99,102,241,0.7)" : "none" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!showDropdown) setHovered(false); }}
    >
      {(hovered || showDropdown) && (
        <div className="absolute z-20" style={{ top: 2, right: 2 }}>
          <button
            ref={triggerRef}
            onClick={() => setShowDropdown((v) => !v)}
            className="flex items-center justify-center"
            style={{ width: 28, height: 28, borderRadius: 6, background: isOwn ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.07)" }}
          >
            <Icon name="chevron" size={12} color={isOwn ? "#FFFFFF" : "#525252"} />
          </button>
        </div>
      )}

      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="flex flex-col"
          style={{
            position: "fixed", zIndex: 9999,
            top: dropdownPos?.top ?? -9999, left: dropdownPos?.left ?? -9999,
            opacity: dropdownPos ? 1 : 0,
            background: "#FFFFFF", boxShadow: "0px 6px 20px rgba(0,0,0,0.12)",
            borderRadius: 10, border: "1px solid #F0F0F0", overflow: "hidden", minWidth: 190,
          }}
        >
          <div className="flex flex-row items-center" style={{ padding: "8px 8px 6px", gap: 1, borderBottom: "1px solid #F5F5F5" }}>
            {EMOJI_LIST.slice(0, 7).map((emoji) => (
              <button
                key={emoji}
                onClick={() => { toggleReaction(discussionId, message.id, emoji); setShowDropdown(false); setHovered(false); }}
                className="hover:bg-neutral-100 rounded-lg flex items-center justify-center"
                style={{ width: 30, height: 30, fontSize: 17 }}
              >
                {emoji}
              </button>
            ))}
          </div>
          {!message.deleted && (
            <button
              onClick={() => { setInfoOpen(true); setShowDropdown(false); setHovered(false); }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#525252" strokeWidth="1.3" /><path d="M8 7.2v3.6" stroke="#525252" strokeWidth="1.3" strokeLinecap="round" /><circle cx="8" cy="5.1" r="0.55" fill="#525252" /></svg>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Infos du message</span>
            </button>
          )}
          <button
            onClick={() => { setReplyTarget(message); setShowDropdown(false); setHovered(false); }}
            className="flex items-center hover:bg-neutral-50 w-full"
            style={{ padding: "9px 14px", gap: 10 }}
          >
            <Icon name="response" size={14} color="#525252" />
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Répondre</span>
          </button>
          {!message.deleted && message.text && (
            <button
              onClick={() => {
                try { navigator.clipboard?.writeText(message.text || ""); } catch (e) { /* noop */ }
                setShowDropdown(false); setHovered(false);
              }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="8" height="9" rx="1.5" stroke="#525252" strokeWidth="1.3" /><path d="M3 11V3.5A1.5 1.5 0 0 1 4.5 2H10" stroke="#525252" strokeWidth="1.3" strokeLinecap="round" /></svg>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Copier</span>
            </button>
          )}
          {!message.deleted && (
            <button
              onClick={() => { setForwardOpen(true); setShowDropdown(false); setHovered(false); }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M9 4l4 4-4 4M13 8H3.5" stroke="#525252" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Transférer</span>
            </button>
          )}
          {!message.deleted && (
            <button
              onClick={() => {
                if (isPinned) unpinMessage(discussionId, message.id);
                else pinMessage(discussionId, message.id);
                setShowDropdown(false); setHovered(false);
              }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <Icon name="pin" size={14} color="#525252" />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>{isPinned ? "Détacher" : "Épingler"}</span>
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setEditText(message.text || ""); setIsEditing(true); setShowDropdown(false); setHovered(false); }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <Icon name="modify" size={14} color="#525252" />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Modifier</span>
            </button>
          )}
          {isOwn && (
            <>
              <div style={{ height: 1, background: "#F5F5F5" }} />
              <button
                onClick={() => { deleteMessage(discussionId, message.id); setShowDropdown(false); setHovered(false); }}
                className="flex items-center hover:bg-red-50 w-full"
                style={{ padding: "9px 14px", gap: 10 }}
              >
                <Icon name="delete" size={14} color="#ef4444" />
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#ef4444" }}>Supprimer</span>
              </button>
            </>
          )}
        </div>,
        document.body
      )}

      {/* Bubble */}
      <div style={{ background: bubbleBg, borderRadius: bubbleRadius, boxShadow: "0px 1px 2px rgba(0,0,0,0.08)", overflow: "hidden" }}>
        {message.reply_to_preview && (
          <div
            onClick={(e) => { e.stopPropagation(); if (message.reply_to_id) onJump?.(message.reply_to_id); }}
            title={message.reply_to_id ? "Aller au message d'origine" : undefined}
            style={{ margin: "6px 8px 0", padding: "6px 10px", background: isOwn ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.04)", borderRadius: 8, borderLeft: "3px solid #525252", maxWidth: 280, cursor: message.reply_to_id ? "pointer" : "default" }}
          >
            <div className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 11, color: isOwn ? "#D4D4D4" : "#525252", marginBottom: 2 }}>
              {message.reply_to_preview.sender_name}
            </div>
            <div style={{ fontFamily: "Poppins", fontSize: 11, color: "#A1A1A1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {message.reply_to_preview.text || "Pièce jointe"}
            </div>
          </div>
        )}

        {imageAtts.length > 0 && (
          <div style={{ margin: message.text ? "6px 4px 0" : 0 }}>
            <ImageAlbum
              images={imageAtts}
              hovered={hovered}
              onOpen={(a) => openLightbox(imageAtts, a)}
              onDownload={(a) => handleDownload(a.url, a.base64Data, a.name || "image.png")}
            />
          </div>
        )}
        {videoAtts.map((a, i) =>
          a.url ? (
            <div key={`v${i}`} style={{ margin: message.text || imageAtts.length ? "6px 4px 0" : 2 }}>
              <VideoBubble url={a.url} />
            </div>
          ) : null
        )}
        {docAtts.map((a, i) =>
          isPdfAtt(a) ? (
            <div key={i} style={{ margin: message.text || i > 0 || imageAtts.length ? "6px 4px 0" : 0 }}>
              <PdfCard
                att={a}
                isOwn={isOwn}
                onOpen={(x) => openLightbox([x], x)}
                onDownload={(x) => handleDownload(x.url, x.base64Data, x.name || "document.pdf")}
              />
            </div>
          ) : (
            <button
              key={i}
              onClick={() => openLightbox([a], a)}
              className="flex flex-row items-center w-full"
              style={{ padding: "8px 12px", gap: 10, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
              title="Ouvrir"
            >
              <Icon name="file" size={18} color={isOwn ? "#FFFFFF" : "#525252"} />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isOwn ? "#FFFFFF" : "#000000", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.name || "Fichier"}
              </span>
              <Icon name="download" size={16} color={isOwn ? "rgba(255,255,255,0.7)" : "#A1A1A1"} />
            </button>
          )
        )}
        {audioAtts.map((a, i) =>
          a.url ? (
            <div key={i} style={{ padding: "6px 8px" }}>
              <audio controls src={a.url} style={{ height: 36 }} />
            </div>
          ) : null
        )}
        {lightboxAtt && createPortal(
          <DocumentPreviewModal
            doc={{ name: lightboxAtt.name || (isImageAtt(lightboxAtt) ? "image.png" : "fichier") }}
            url={lightboxAtt.base64Data || lightboxAtt.url || ""}
            onClose={() => setLightbox(null)}
            onDownload={() => handleDownload(lightboxAtt.url, lightboxAtt.base64Data, lightboxAtt.name || "fichier")}
            onPrev={lightbox && lightbox.list.length > 1 ? () => lightboxStep(-1) : undefined}
            onNext={lightbox && lightbox.list.length > 1 ? () => lightboxStep(1) : undefined}
            counter={lightbox && lightbox.list.length > 1 ? `${lightbox.idx + 1} / ${lightbox.list.length}` : undefined}
          />,
          document.body
        )}

        {message.poll && (
          <div style={{ padding: "10px 12px 2px" }}>
            <PollCard
              message={message}
              isOwn={isOwn}
              currentUserId={currentUserId}
              members={members}
              onVote={(optId) => votePoll(discussionId, message.id, optId)}
              onClose={() => closePoll(discussionId, message.id)}
            />
          </div>
        )}

        <div style={{ padding: "8px 12px 6px" }}>
          {isEditing ? (
            <>
              <textarea
                autoFocus
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === "Escape") setIsEditing(false);
                }}
                className="w-full outline-none resize-none bg-transparent"
                style={{ fontFamily: "Poppins", fontSize: 14, lineHeight: "20px", color: isOwn ? "#FFFFFF" : "#000000", minHeight: 40, whiteSpace: "pre-wrap" }}
              />
              <div className="flex justify-end items-center" style={{ gap: 8, marginTop: 6 }}>
                <button onClick={() => setIsEditing(false)} className="hover:underline" style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: isOwn ? "#A3A3A3" : "#6B7280" }}>Annuler</button>
                <button onClick={saveEdit} className="font-semibold hover:underline" style={{ fontFamily: "Poppins", fontSize: 12, color: isOwn ? "#FFFFFF" : "#000000" }}>Sauvegarder</button>
              </div>
            </>
          ) : (
            message.text && (
              <span style={{ fontFamily: "Poppins", fontWeight: 400, fontSize: 14, lineHeight: "20px", color: isOwn ? "#FFFFFF" : "#000000", whiteSpace: "pre-wrap" }}>
                <RenderMessageText text={message.text} mentions={message.mentions} members={members} folderRefs={message.folder_refs} navigateWorkspace={navigateWorkspace} isOwn={isOwn} />
              </span>
            )
          )}
          {showTimestamp && !isEditing && (
            <div className="flex justify-end items-center" style={{ marginTop: 2, gap: 4 }}>
              {isPinned && (
                <span title="Message épinglé" style={{ display: "inline-flex", marginRight: 2 }}>
                  <Icon name="pin" size={11} color={isOwn ? "#A3A3A3" : "#A1A1A1"} />
                </span>
              )}
              {message.edited && (
                <span className="italic" style={{ fontFamily: "Poppins", fontSize: 11, color: isOwn ? "#A3A3A3" : "#A1A1A1" }}>modifié</span>
              )}
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: isOwn ? "#A3A3A3" : "#A1A1A1", marginLeft: message.edited ? 0 : 12 }}>
                {formatTime(message.created_at)}
              </span>
              {isOwn && readStatus && <ReadTicks status={readStatus} />}
            </div>
          )}
        </div>
      </div>

      {hasReactions && (
        <div
          className="flex flex-row flex-wrap items-center absolute"
          style={{
            bottom: 0,
            ...(isOwn ? { right: 8 } : { left: 8 }),
            gap: 1,
            background: "rgba(255,255,255,0.75)",
            borderRadius: 12,
            padding: "1px 6px",
            boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
          }}
        >
          {Object.entries(grouped).map(([emoji, userIds]) => (
            <ReactionPill
              key={emoji}
              emoji={emoji}
              userIds={userIds}
              isMine={userIds.includes(currentUserId)}
              onClick={() => setInfoOpen(true)}
            />
          ))}
        </div>
      )}

      {infoOpen && (
        <MessageInfoModal
          message={message}
          members={members}
          readReceipts={readReceipts}
          allMessages={allMessages}
          discussionMembers={discussionMembers}
          currentUserId={currentUserId}
          onClose={() => setInfoOpen(false)}
        />
      )}
      {forwardOpen && (
        <ForwardModal
          currentDiscussionId={discussionId}
          onForward={(targetId) =>
            sendMessage(targetId, {
              text: message.text || undefined,
              attachments:
                message.attachments && message.attachments.length
                  ? message.attachments
                  : undefined,
              attachment: message.attachment || undefined,
            })
          }
          onClose={() => setForwardOpen(false)}
        />
      )}
    </div>
  );
}, (a, b) =>
  // Ignore the unstable function props (navigateWorkspace / onJump); re-render
  // only when the message data or its rendered status actually changes.
  a.message === b.message &&
  a.position === b.position &&
  a.showTimestamp === b.showTimestamp &&
  a.readStatus === b.readStatus &&
  a.isOwn === b.isOwn &&
  a.members === b.members &&
  a.discussionId === b.discussionId &&
  a.readReceipts === b.readReceipts &&
  a.allMessages === b.allMessages &&
  a.discussionMembers === b.discussionMembers &&
  a.currentUserId === b.currentUserId &&
  a.highlight === b.highlight &&
  a.isPinned === b.isPinned
);

// --- A group of consecutive bubbles from the same sender ---
// Memoized: ignores the unstable navigateWorkspace/onJump props so that
// unrelated state changes (e.g. the typing indicator) don't repaint the
// whole thread.
const MessageGroup = React.memo(function MessageGroup({ group, members, discussion, allMessages, readReceipts, currentUserId, navigateWorkspace, onJump, highlightId, pinnedIds }) {
  const sender = getMember(members, group.senderId) || { id: group.senderId, name: group.messages[0]?.sender_name || "Inconnu" };
  const count = group.messages.length;

  const getReadStatus = (msg) => {
    const memberIds = (discussion?.members || []).filter((id) => id !== currentUserId);
    if (!memberIds.length) return "sent";
    const idx = allMessages.findIndex((m) => m.id === msg.id);
    const others = (readReceipts || []).filter((r) => r.user_id !== currentUserId);
    let read = 0;
    for (const r of others) {
      const ri = allMessages.findIndex((m) => m.id === r.last_read_message_id);
      if (ri >= idx) read++;
    }
    if (read === 0) return "sent";
    return read >= memberIds.length ? "read_all" : "read_some";
  };

  return (
    <div className="flex flex-row" style={{ justifyContent: group.isOwn ? "flex-end" : "flex-start", gap: 8 }}>
      {!group.isOwn && (
        <div className="flex-shrink-0" style={{ width: 32, paddingTop: 2 }}>
          <MemberAvatar member={sender} size={32} />
        </div>
      )}
      <div className="flex flex-col" style={{ alignItems: group.isOwn ? "flex-end" : "flex-start", gap: 3, maxWidth: "65%" }}>
        {!group.isOwn && (
          <span className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 12, color: "#525252", marginBottom: 2, marginLeft: 4 }}>
            {sender.name}
          </span>
        )}
        {group.messages.map((msg, i) => {
          const position = count === 1 ? "solo" : i === 0 ? "first" : i === count - 1 ? "last" : "middle";
          const showTimestamp = i === count - 1;
          return (
            <MessageBubble
              key={msg.id}
              message={msg}
              position={position}
              showTimestamp={showTimestamp}
              isOwn={group.isOwn}
              members={members}
              discussionId={discussion?.id}
              readStatus={group.isOwn && showTimestamp ? getReadStatus(msg) : undefined}
              readReceipts={readReceipts}
              allMessages={allMessages}
              discussionMembers={discussion?.members}
              navigateWorkspace={navigateWorkspace}
              onJump={onJump}
              highlight={msg.id === highlightId}
              isPinned={pinnedIds?.has(msg.id)}
              currentUserId={currentUserId}
            />
          );
        })}
      </div>
    </div>
  );
}, (a, b) =>
  a.group === b.group &&
  a.members === b.members &&
  a.discussion === b.discussion &&
  a.allMessages === b.allMessages &&
  a.readReceipts === b.readReceipts &&
  a.currentUserId === b.currentUserId &&
  a.highlightId === b.highlightId &&
  a.pinnedIds === b.pinnedIds
);

// Small on/off toggle used in the poll composer.
function Toggle({ label, checked, onChange }) {
  return (
    <button
      onClick={onChange}
      className="flex flex-row items-center justify-between hover:bg-neutral-50 w-full"
      style={{ padding: "6px 4px", borderRadius: 8 }}
    >
      <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#1E1E1E" }}>{label}</span>
      <span style={{ width: 38, height: 22, borderRadius: 11, background: checked ? "#6366f1" : "#D4D4D4", position: "relative", transition: "background 0.15s", flexShrink: 0 }}>
        <span style={{ position: "absolute", top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: 9, background: "#FFFFFF", transition: "left 0.15s" }} />
      </span>
    </button>
  );
}

// Modal to compose a poll (question + 2–12 options + multi/anonymous flags).
function PollComposerModal({ onClose, onCreate }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setOption = (i, v) => setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  const addOption = () => setOptions((prev) => (prev.length >= 12 ? prev : [...prev, ""]));
  const removeOption = (i) =>
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));

  const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
  const valid = question.trim() && cleanOptions.length >= 2;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onCreate({ question: question.trim(), options: cleanOptions, multi, anonymous });
      onClose();
    } catch (e) {
      console.warn("[messagerie] create poll failed:", e?.message);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <div className="bg-white shadow-2xl flex flex-col overflow-hidden" style={{ width: 420, maxWidth: "92vw", maxHeight: "88vh", borderRadius: 16 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-row items-center justify-between border-b border-neutral-200 flex-shrink-0" style={{ padding: "12px 16px" }}>
          <span className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 15 }}>Nouveau sondage</span>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg hover:bg-neutral-100" style={{ width: 30, height: 30 }}>
            <Icon name="close" size={16} color="#525252" />
          </button>
        </div>
        <div className="flex flex-col overflow-y-auto" style={{ padding: 16, gap: 14 }}>
          <input
            autoFocus
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Posez votre question…"
            maxLength={300}
            className="w-full outline-none"
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, background: "#F5F5F5", borderRadius: 10, padding: "10px 12px" }}
          />
          <div className="flex flex-col" style={{ gap: 8 }}>
            {options.map((o, i) => (
              <div key={i} className="flex flex-row items-center" style={{ gap: 8 }}>
                <input
                  value={o}
                  onChange={(e) => setOption(i, e.target.value)}
                  placeholder={`Option ${i + 1}`}
                  maxLength={120}
                  className="flex-1 outline-none"
                  style={{ fontFamily: "Poppins", fontSize: 13, background: "#F5F5F5", borderRadius: 8, padding: "8px 12px" }}
                />
                {options.length > 2 && (
                  <button onClick={() => removeOption(i)} className="flex items-center justify-center hover:bg-neutral-100 rounded-md flex-shrink-0" style={{ width: 28, height: 28 }}>
                    <Icon name="close" size={12} color="#A1A1A1" />
                  </button>
                )}
              </div>
            ))}
            {options.length < 12 && (
              <button onClick={addOption} className="flex flex-row items-center hover:opacity-80" style={{ gap: 6, padding: "4px 2px" }}>
                <Icon name="plus" size={12} color="#6366f1" />
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#6366f1" }}>Ajouter une option</span>
              </button>
            )}
          </div>
          <div className="flex flex-col" style={{ gap: 4 }}>
            <Toggle label="Choix multiple" checked={multi} onChange={() => setMulti((v) => !v)} />
            <Toggle label="Vote anonyme" checked={anonymous} onChange={() => setAnonymous((v) => !v)} />
          </div>
        </div>
        <div className="flex flex-row justify-end border-t border-neutral-200" style={{ padding: "12px 16px", gap: 10 }}>
          <button onClick={onClose} className="hover:bg-neutral-100" style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#525252", padding: "9px 16px", borderRadius: 10 }}>Annuler</button>
          <button onClick={submit} disabled={!valid || busy} style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#FFFFFF", background: !valid ? "#C7C7C7" : "#000000", padding: "9px 18px", borderRadius: 10 }}>
            {busy ? "Envoi…" : "Créer"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// --- Composer ---
function ChatInput({ discussionId, members, orgId }) {
  const sendMessage = useMessagerieStore((s) => s.sendMessage);
  const uploadAttachment = useMessagerieStore((s) => s.uploadAttachment);
  const replyTarget = useMessagerieStore((s) => s.replyTarget);
  const setReplyTarget = useMessagerieStore((s) => s.setReplyTarget);
  const sendTyping = useMessagerieStore((s) => s.sendTyping);
  const clearTyping = useMessagerieStore((s) => s.clearTyping);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(0);

  const [inputText, setInputText] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionStart, setMentionStart] = useState(0);
  const [trackedMentions, setTrackedMentions] = useState([]);
  // Folder tagging ("/folder")
  const [folderQuery, setFolderQuery] = useState(null);
  const [folderStart, setFolderStart] = useState(0);
  const [folderResults, setFolderResults] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [trackedFolders, setTrackedFolders] = useState([]);
  const folderDebounceRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Optimistic upload cards (thumbnail + live progress) shown above the composer.
  // [{ id, name, isVideo, previewUrl, progress }]
  const [uploads, setUploads] = useState([]);
  // Anti double-envoi : un ref synchrone (les deux events Enter+clic peuvent
  // arriver dans le même tick, avant qu'un setState ne soit appliqué) + un état
  // pour désactiver le bouton/Entrée pendant l'envoi.
  const sendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const inputRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachRef = useRef(null);

  // Debounced Algolia folder search for the "/" tag dropdown.
  useEffect(() => {
    if (folderQuery === null) { setFolderResults([]); setFolderLoading(false); return; }
    setFolderLoading(true);
    if (folderDebounceRef.current) clearTimeout(folderDebounceRef.current);
    folderDebounceRef.current = setTimeout(async () => {
      try {
        const filters = orgId ? [{ field: "master_id", operator: "==", value: orgId }] : [];
        const results = await queryCollectionAlgolia("folder", filters, folderQuery, 6);
        setFolderResults(Array.isArray(results) ? results : []);
      } catch (e) {
        setFolderResults([]);
      } finally {
        setFolderLoading(false);
      }
    }, 250);
    return () => { if (folderDebounceRef.current) clearTimeout(folderDebounceRef.current); };
  }, [folderQuery, orgId]);

  const pickFolder = (folder) => {
    const id = folder.id || folder.objectID;
    const name = folder.nom || folder.name || "Dossier";
    const before = inputText.substring(0, folderStart);
    const after = inputText.substring(folderStart + 1 + (folderQuery?.length || 0));
    setInputText(`${before}/${name} ${after}`);
    setFolderQuery(null);
    setTrackedFolders((prev) =>
      prev.some((f) => f.id === id) ? prev : [...prev, { id, name }]
    );
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (!showAttach) return;
    function onDoc(e) {
      if (attachRef.current && !attachRef.current.contains(e.target)) setShowAttach(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showAttach]);

  const matching = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return (members || [])
      .map((u) => getMember([u], u.id))
      .filter((m) => m && m.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, members]);

  useEffect(() => { if (replyTarget) inputRef.current?.focus(); }, [replyTarget]);

  // Stop the typing indicator when leaving the discussion / unmounting.
  useEffect(() => {
    const id = discussionId;
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (id) clearTyping(id);
    };
  }, [discussionId, clearTyping]);

  const doSend = async () => {
    if (!inputText.trim()) return;
    // Si un envoi est déjà en cours, on ignore (évite que l'utilisateur, croyant
    // que ça n'est pas parti, ne renvoie 2× le même message).
    if (sendingRef.current) return;
    sendingRef.current = true;
    setIsSending(true);

    // Snapshot du contenu, puis on vide l'input tout de suite : feedback immédiat
    // « c'est parti ». Restauré si l'envoi échoue.
    const text = inputText;
    const replyToId = replyTarget?.id;
    const mentions = trackedMentions.length ? trackedMentions : undefined;
    const folderRefs = trackedFolders.length ? trackedFolders : undefined;
    setInputText("");
    setTrackedMentions([]);
    setTrackedFolders([]);
    setMentionQuery(null);
    setFolderQuery(null);
    setReplyTarget(null);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    clearTyping(discussionId);
    if (inputRef.current) { inputRef.current.style.height = "auto"; }

    try {
      await sendMessage(discussionId, { text, replyToId, mentions, folderRefs });
    } catch (e) {
      // Échec : on rend le texte à l'utilisateur pour qu'il puisse réessayer.
      console.warn("[messagerie] send failed:", e?.message);
      setInputText(text);
      if (replyToId) setReplyTarget(replyTarget);
      if (mentions) setTrackedMentions(mentions);
      if (folderRefs) setTrackedFolders(folderRefs);
    } finally {
      sendingRef.current = false;
      setIsSending(false);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const onChange = (e) => {
    const value = e.target.value;
    setInputText(value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";

    // Typing indicator: throttle sendTyping to ~2s, auto-clear after 3s idle.
    if (discussionId && value.trim()) {
      const now = Date.now();
      if (now - lastTypingRef.current > 2000) {
        sendTyping(discussionId);
        lastTypingRef.current = now;
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => clearTyping(discussionId), 3000);
    } else if (discussionId) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      clearTyping(discussionId);
    }

    const cursor = e.target.selectionStart || value.length;
    const before = value.substring(0, cursor);

    const at = before.lastIndexOf("@");
    if (at >= 0) {
      const cb = at > 0 ? before[at - 1] : " ";
      if (cb === " " || cb === "\n" || at === 0) {
        const q = before.substring(at + 1);
        if (!q.includes(" ")) { setMentionQuery(q); setMentionStart(at); setFolderQuery(null); return; }
      }
    }
    setMentionQuery(null);

    // "/folder" tagging
    const slash = before.lastIndexOf("/");
    if (slash >= 0) {
      const cb = slash > 0 ? before[slash - 1] : " ";
      if (cb === " " || cb === "\n" || slash === 0) {
        const q = before.substring(slash + 1);
        if (!q.includes(" ")) { setFolderQuery(q); setFolderStart(slash); return; }
      }
    }
    setFolderQuery(null);
  };

  const pickMention = (m) => {
    const before = inputText.substring(0, mentionStart);
    const after = inputText.substring(mentionStart + 1 + (mentionQuery?.length || 0));
    setInputText(`${before}@${m.name} ${after}`);
    setMentionQuery(null);
    setTrackedMentions((prev) => (prev.includes(m.id) ? prev : [...prev, m.id]));
    inputRef.current?.focus();
  };

  const onFile = async (e, kind) => {
    const allFiles = Array.from(e.target.files || []);
    e.target.value = "";
    if (!allFiles.length) return;
    // Mirror the backend MAX_CHAT_FILE_SIZE (250MB): reject oversized files up
    // front with a clear message instead of letting the upload fail server-side.
    const MAX = 250 * 1024 * 1024;
    const tooBig = allFiles.filter((f) => f.size > MAX);
    const files = allFiles.filter((f) => f.size <= MAX);
    if (tooBig.length) {
      window.alert(
        tooBig.length === 1
          ? `« ${tooBig[0].name} » dépasse la limite de 250 Mo et n'a pas été envoyé.`
          : `${tooBig.length} fichiers dépassent la limite de 250 Mo et n'ont pas été envoyés.`
      );
    }
    if (!files.length) return;

    // One optimistic card per file (local thumbnail + 0% bar), updated live as
    // each upload streams. Cleared once the message is sent.
    const entries = files.map((f, i) => ({
      id: `up-${Date.now()}-${i}`,
      name: f.name,
      isVideo: (f.type || "").startsWith("video/"),
      previewUrl: URL.createObjectURL(f),
      progress: 0,
    }));
    setUploads((prev) => [...prev, ...entries]);
    const setProgress = (id, p) =>
      setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, progress: p } : u)));

    setBusy(true);
    try {
      const attachments = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const entry = entries[i];
        const att = await uploadAttachment(discussionId, file, (p) =>
          setProgress(entry.id, p)
        );
        setProgress(entry.id, 1);
        attachments.push({
          // Classify by real mime: images stay "image", videos/others go to
          // "file" (rendered inline via <video> when the mime is video/*).
          type: (file.type || "").startsWith("image/") ? "image" : "file",
          name: att.name,
          gcs_path: att.gcs_path,
          size: att.size,
          mime_type: att.mime_type,
          preview_gcs_path: att.preview_gcs_path,
          pages: att.pages,
        });
      }
      await sendMessage(discussionId, {
        text: null,
        attachments,
        replyToId: replyTarget?.id,
      });
      setReplyTarget(null);
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
      entries.forEach((en) => URL.revokeObjectURL(en.previewUrl));
      setUploads((prev) => prev.filter((u) => !entries.some((en) => en.id === u.id)));
    }
  };

  const replySender = replyTarget ? getMember(members, replyTarget.sender_id) : null;

  return (
    <div className="flex flex-col justify-end" style={{ flexShrink: 0 }}>
      {replyTarget && (
        <div className="flex flex-row items-center justify-between" style={{ padding: "8px 12px", marginBottom: 4, background: "#F5F5F5", borderRadius: "10px 10px 0 0", borderLeft: "3px solid #525252" }}>
          <div className="flex flex-col" style={{ gap: 2, overflow: "hidden", flex: 1 }}>
            <span className="font-semibold" style={{ fontFamily: "Poppins", fontSize: 12, color: "#525252" }}>{replySender?.name || replyTarget.sender_name}</span>
            <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#A1A1A1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{replyTarget.text || "Pièce jointe"}</span>
          </div>
          <button onClick={() => setReplyTarget(null)} className="flex-shrink-0 hover:bg-neutral-200 rounded-full flex items-center justify-center" style={{ width: 24, height: 24 }}>
            <Icon name="close" size={10} color="#A1A1A1" />
          </button>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6, marginBottom: 6 }}>
          {uploads.map((u) => {
            const pct = Math.round((u.progress || 0) * 100);
            const done = pct >= 100;
            return (
              <div
                key={u.id}
                className="flex flex-row items-center"
                style={{ gap: 10, padding: 8, background: "#EEF0FF", borderRadius: 10 }}
              >
                <div
                  style={{ position: "relative", width: 44, height: 44, borderRadius: 8, overflow: "hidden", background: "#000000", flexShrink: 0 }}
                >
                  {u.isVideo ? (
                    <video src={u.previewUrl} muted preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <img src={u.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  )}
                  {!done && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
                      <div
                        className="animate-spin"
                        style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#FFFFFF", borderRadius: "50%" }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 5 }}>
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1F2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.name}
                  </span>
                  <div style={{ height: 6, borderRadius: 3, background: "#C7CCF5", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        borderRadius: 3,
                        background: "linear-gradient(90deg, #464DFF, #7C82FF)",
                        transition: "width 0.25s ease",
                      }}
                    />
                  </div>
                </div>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#464DFF", minWidth: 36, textAlign: "right" }}>
                  {done ? "Envoi…" : `${pct}%`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-row items-center relative" style={{ padding: "8px 10px", gap: 10, background: "#F5F5F5", borderRadius: replyTarget ? "0 0 10px 10px" : 10 }}>
        <div className="relative" ref={attachRef}>
          <button onClick={() => setShowAttach((v) => !v)} disabled={busy} className="hover:bg-neutral-100 rounded-md flex items-center justify-center" style={{ width: 28, height: 28 }}>
            <Icon name="plus" size={12} color="#A1A1A1" />
          </button>
          {showAttach && (
            <div className="absolute z-50 flex flex-col" style={{ bottom: "100%", left: 0, marginBottom: 8, background: "#FFFFFF", boxShadow: "0px 4px 12px rgba(0,0,0,0.15)", borderRadius: 10, overflow: "hidden", minWidth: 160 }}>
              <button onClick={() => { setShowAttach(false); imageInputRef.current?.click(); }} className="flex flex-row items-center hover:bg-neutral-50" style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000000" }}>
                <Icon name="image" size={16} color="#A1A1A1" /> Image / Vidéo
              </button>
              <button onClick={() => { setShowAttach(false); fileInputRef.current?.click(); }} className="flex flex-row items-center hover:bg-neutral-50" style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000000" }}>
                <Icon name="join" size={16} color="#A1A1A1" /> Fichier
              </button>
              <button onClick={() => { setShowAttach(false); setShowPoll(true); }} className="flex flex-row items-center hover:bg-neutral-50" style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000000" }}>
                <Icon name="poll" size={16} color="#A1A1A1" /> Sondage
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (mentionQuery !== null && matching.length) pickMention(matching[0]);
                else if (folderQuery !== null && folderResults.length) pickFolder(folderResults[0]);
                else doSend();
              }
              if (e.key === "Escape") {
                if (mentionQuery !== null) setMentionQuery(null);
                if (folderQuery !== null) setFolderQuery(null);
              }
            }}
            placeholder="Entrez un message"
            rows={1}
            className="w-full outline-none border-none resize-none"
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000000", background: "transparent", maxHeight: 120, overflowY: "auto" }}
          />
          {mentionQuery !== null && matching.length > 0 && (
            <div className="absolute z-50 flex flex-col" style={{ bottom: "100%", left: 0, marginBottom: 12, background: "#FFFFFF", boxShadow: "0px 4px 12px rgba(0,0,0,0.15)", borderRadius: 10, overflow: "hidden", minWidth: 200, maxHeight: 200, overflowY: "auto" }}>
              {matching.map((m) => (
                <button key={m.id} onClick={() => pickMention(m)} className="flex flex-row items-center hover:bg-neutral-50" style={{ padding: "8px 12px", gap: 10 }}>
                  <MemberAvatar member={m} size={24} />
                  <div className="flex flex-col items-start">
                    <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000" }}>{m.name}</span>
                    {m.role && <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#A1A1A1" }}>{m.role}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {folderQuery !== null && (folderLoading || folderResults.length > 0) && (
            <div className="absolute z-50 flex flex-col" style={{ bottom: "100%", left: 0, marginBottom: 12, background: "#FFFFFF", boxShadow: "0px 4px 12px rgba(0,0,0,0.15)", borderRadius: 10, overflow: "hidden", minWidth: 240, maxHeight: 240, overflowY: "auto" }}>
              {folderLoading && (
                <div className="flex flex-row items-center" style={{ padding: "8px 12px", gap: 8 }}>
                  <div className="animate-spin" style={{ width: 14, height: 14, border: "2px solid #ddd", borderTopColor: "#464DFF", borderRadius: "50%" }} />
                  <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Recherche…</span>
                </div>
              )}
              {!folderLoading &&
                folderResults.map((f) => (
                  <button key={f.id || f.objectID} onClick={() => pickFolder(f)} className="flex flex-row items-center hover:bg-neutral-50 w-full" style={{ padding: "8px 12px", gap: 10, textAlign: "left" }}>
                    <Icon name="file" size={16} color="#4F46E5" />
                    <div className="flex flex-col items-start" style={{ overflow: "hidden" }}>
                      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nom || f.name || "Dossier"}</span>
                      {(f.ville || f.marque) && (
                        <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#A1A1A1" }}>{[f.marque, f.ville].filter(Boolean).join(" · ")}</span>
                      )}
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>

        <button onClick={doSend} disabled={busy || isSending} className="hover:bg-neutral-100 rounded-md flex items-center justify-center" style={{ width: 28, height: 28, opacity: isSending ? 0.5 : 1 }}>
          <Icon name="send" size={16} color={inputText.trim() ? "#525252" : "#A1A1A1"} />
        </button>
      </div>

      <input ref={imageInputRef} type="file" accept="image/*,video/*" multiple onChange={(e) => onFile(e, "image")} style={{ display: "none" }} />
      <input ref={fileInputRef} type="file" multiple onChange={(e) => onFile(e, "file")} style={{ display: "none" }} />

      {showPoll && (
        <PollComposerModal
          onClose={() => setShowPoll(false)}
          onCreate={(poll) => sendMessage(discussionId, { poll })}
        />
      )}
    </div>
  );
}

// --- Pinned messages banner (WhatsApp-style) ---
// One pinned → a single bar that jumps to the message. Several → the bar shows
// the most recent and a count; clicking expands the full list. Each row jumps
// to its message and has an unpin (×) control.
function PinnedBar({ pinned, onJump, onUnpin }) {
  const [expanded, setExpanded] = useState(false);
  if (!pinned || !pinned.length) return null;

  // Newest pin first (the array is appended chronologically).
  const list = [...pinned].reverse();
  const count = list.length;
  const latest = list[0];

  const previewOf = (p) => p.text || (p.attachment_type === "image" ? "Image" : p.attachment_type === "audio" ? "Audio" : p.attachment_type ? "Pièce jointe" : "Message");

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <div
        className="flex flex-row items-center"
        onClick={() => (count > 1 ? setExpanded((v) => !v) : onJump(latest.message_id))}
        style={{ gap: 10, padding: "8px 12px", background: "#FFFFFF", border: "1px solid #ECECEC", borderLeft: "3px solid #6366f1", borderRadius: 8, cursor: "pointer" }}
      >
        <Icon name="pin" size={15} color="#6366f1" />
        <div className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#6366f1" }}>
            {count > 1 ? `${count} messages épinglés` : "Message épinglé"}
          </span>
          <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#525252", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {latest.sender_name ? `${latest.sender_name} : ` : ""}{previewOf(latest)}
          </span>
        </div>
        {count > 1 ? (
          <span style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s", display: "inline-flex" }}>
            <Icon name="chevron" size={12} color="#A1A1A1" />
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onUnpin(latest.message_id); }}
            title="Détacher"
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ width: 24, height: 24 }}
          >
            <Icon name="close" size={10} color="#A1A1A1" />
          </button>
        )}
      </div>

      {expanded && count > 1 && (
        <div className="flex flex-col" style={{ position: "absolute", zIndex: 30, top: "100%", left: 0, right: 0, marginTop: 4, background: "#FFFFFF", border: "1px solid #ECECEC", borderRadius: 10, boxShadow: "0px 6px 20px rgba(0,0,0,0.12)", overflow: "hidden", maxHeight: 260, overflowY: "auto" }}>
          {list.map((p) => (
            <div key={p.message_id} className="flex flex-row items-center hover:bg-neutral-50" style={{ gap: 10, padding: "9px 12px", borderBottom: "1px solid #F5F5F5" }}>
              <Icon name="pin" size={13} color="#A1A1A1" />
              <button
                onClick={() => { setExpanded(false); onJump(p.message_id); }}
                className="flex flex-col items-start"
                style={{ flex: 1, minWidth: 0, background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
              >
                {p.sender_name && (
                  <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#525252" }}>{p.sender_name}</span>
                )}
                <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#525252", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                  {previewOf(p)}
                </span>
              </button>
              <button
                onClick={() => onUnpin(p.message_id)}
                title="Détacher"
                className="hover:bg-neutral-100 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ width: 24, height: 24 }}
              >
                <Icon name="close" size={10} color="#A1A1A1" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main thread panel ---
export default function MessagerieThread({ discussion, userId, orgId, navigateWorkspace }) {
  const messagesByDisc = useMessagerieStore((s) => s.messages);
  const isLoading = useMessagerieStore((s) => s.isLoadingMessages);
  const readReceiptsByDisc = useMessagerieStore((s) => s.readReceipts);
  const members = useMessagerieStore((s) => s.members);
  const loadOlderMessages = useMessagerieStore((s) => s.loadOlderMessages);
  const ingestLiveMessages = useMessagerieStore((s) => s.ingestLiveMessages);
  const fetchReadReceipts = useMessagerieStore((s) => s.fetchReadReceipts);
  const isLoadingOlder = useMessagerieStore((s) => s.isLoadingOlderMessages);
  const hasMoreByDisc = useMessagerieStore((s) => s.hasMoreMessages);
  const addMembers = useMessagerieStore((s) => s.addMembers);
  const leaveDiscussion = useMessagerieStore((s) => s.leaveDiscussion);
  const unpinMessage = useMessagerieStore((s) => s.unpinMessage);

  const [convSearch, setConvSearch] = useState("");
  const [showConvSearch, setShowConvSearch] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  // Id of the last message we auto-scrolled for. Keyed on id (not count) because
  // the live window is capped at 40: in a busy thread a new incoming message
  // rotates the oldest out, so messages.length stays flat and a count-based
  // check never fires (the message lands just below the fold). null = thread not
  // yet primed -> first load scrolls to bottom.
  const lastSeenIdRef = useRef(null);
  const jumpingRef = useRef(false);
  const [highlightId, setHighlightId] = useState(null);

  const discId = discussion?.id;
  const messages = useMemo(() => messagesByDisc[discId] || [], [messagesByDisc, discId]);
  const pinnedMessages = useMemo(() => discussion?.pinned_messages || [], [discussion]);
  const pinnedIds = useMemo(() => new Set(pinnedMessages.map((p) => p.message_id)), [pinnedMessages]);
  // Stable reference so memoized MessageGroups don't re-render on every parent render.
  const readReceipts = useMemo(() => readReceiptsByDisc[discId] || [], [readReceiptsByDisc, discId]);
  const chatMembers = useMemo(
    () => (discussion ? (members || []).filter((m) => discussion.members.includes(m.id)).map((u) => getMember([u], u.id)) : []),
    [discussion, members]
  );

  useEffect(() => { setShowConvSearch(false); setConvSearch(""); lastSeenIdRef.current = null; }, [discId]);

  // Real-time: Firestore listener on the recent message window. Instead of
  // re-pulling the whole window over HTTP on every change (the old heric
  // pattern), we merge the snapshot docs straight into the store incrementally
  // — text, reactions, edits, replies, polls and deletions all apply with zero
  // network. The store resolves signed attachment URLs lazily (one small fetch)
  // only when a message actually carries an unresolved attachment.
  // We watch the recent window (not just the newest message) so reactions /
  // edits / deletes on the latest messages are also picked up live.
  useEffect(() => {
    if (!discId) return;
    const q = query(
      collection(db, "discussions", discId, "messages"),
      orderBy("created_at", "desc"),
      fsLimit(40)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.docChanges().length === 0) return;
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const removed = snap
          .docChanges()
          .filter((c) => c.type === "removed")
          .map((c) => c.doc.id);
        ingestLiveMessages(discId, docs, removed);
      },
      (err) => console.warn("[messagerie] messages listener error:", err)
    );
    return () => unsub();
  }, [discId, ingestLiveMessages]);

  // Real-time read receipts: when anyone (e.g. on mobile) reads up to a new
  // message, their receipt doc changes — refetch so the blue ticks update live.
  useEffect(() => {
    if (!discId) return;
    const q = query(collection(db, "discussions", discId, "read_receipts"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.docChanges().length === 0) return;
        fetchReadReceipts(discId);
      },
      (err) => console.warn("[messagerie] read receipts listener error:", err)
    );
    return () => unsub();
  }, [discId, fetchReadReceipts]);

  // Real-time typing indicator (heric mechanism): listen to the discussion's
  // `typing` subcollection; show first names of users typing (skip self + stale).
  const [typingNames, setTypingNames] = useState([]);
  useEffect(() => {
    if (!discId) { setTypingNames([]); return; }
    const q = query(collection(db, "typing_status"), where("discussion_id", "==", discId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const names = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          if (!data.user_id || data.user_id === userId) return;
          const ts = data.timestamp;
          if (ts?.toMillis && now - ts.toMillis() > 10000) return;
          // Prefer the name embedded in the doc; fall back to our members list.
          const m = getMember(members, data.user_id);
          const name = (data.user_name || "").trim() || (m ? m.name : "");
          if (name) names.push(name.split(" ")[0]);
        });
        setTypingNames(names);
      },
      (err) => console.warn("[messagerie] typing listener error:", err)
    );
    return () => { unsub(); setTypingNames([]); };
  }, [discId, userId, members]);

  // Auto-scroll to the newest message. Keyed on the LAST message's id rather
  // than on messages.length: once the 40-message live window is full an incoming
  // message rotates the oldest out, so the length stays constant and a
  // count-based trigger never fired — received messages silently landed below
  // the fold ("le msg n'arrive pas" until you re-open the conversation).
  const lastMessageId = messages.length ? messages[messages.length - 1].id : null;
  useEffect(() => {
    if (!lastMessageId || jumpingRef.current) return;
    const firstLoad = lastSeenIdRef.current === null;
    lastSeenIdRef.current = lastMessageId;
    const el = scrollRef.current;
    // Always snap to the bottom on the first load of a thread; afterwards only
    // follow new messages if the reader is already near the bottom (don't yank
    // them away while they're scrolled up reading history).
    const nearBottom =
      !el || el.scrollHeight - el.scrollTop - el.clientHeight < 250;
    if (firstLoad || nearBottom) endRef.current?.scrollIntoView({ behavior: "auto" });
  }, [lastMessageId]);

  const filtered = convSearch
    ? messages.filter((m) => m.text && m.text.toLowerCase().includes(convSearch.toLowerCase()))
    : messages;
  const groups = useMemo(() => groupMessages(filtered, userId), [filtered, userId]);

  if (!discussion) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: "#FAFAFA" }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 16, color: "#A1A1A1" }}>Sélectionnez une discussion</span>
      </div>
    );
  }

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 100 && hasMoreByDisc[discId] !== false && !isLoadingOlder) {
      const prev = el.scrollHeight;
      loadOlderMessages(discId).then(() => {
        requestAnimationFrame(() => {
          if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prev;
        });
      });
    }
  };

  // Jump to the original of a replied-to message. If it isn't loaded yet, page
  // back through history (50 at a time) until it appears or we reach the top,
  // then scroll to it and flash a highlight.
  const jumpToMessage = async (targetId) => {
    if (!targetId) return;
    // A conversation search filters the rendered list — clear it so the target
    // is actually present in the DOM.
    setConvSearch("");
    setShowConvSearch(false);
    jumpingRef.current = true;

    const loaded = () =>
      (useMessagerieStore.getState().messages[discId] || []).some((m) => m.id === targetId);
    const topReached = () =>
      useMessagerieStore.getState().hasMoreMessages[discId] === false;

    let guard = 0;
    while (!loaded() && !topReached() && guard < 100) {
      await loadOlderMessages(discId);
      guard++;
    }

    // Wait for the prepended messages to render, then scroll + highlight.
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(`msg-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(targetId);
        setTimeout(() => setHighlightId((c) => (c === targetId ? null : c)), 2000);
        jumpingRef.current = false;
      } else if (attempts < 30) {
        requestAnimationFrame(() => tryScroll(attempts + 1));
      } else {
        jumpingRef.current = false;
      }
    };
    requestAnimationFrame(() => tryScroll());
  };

  return (
    <div className="flex flex-col flex-1" style={{ padding: "30px 20px", gap: 15, background: "#FAFAFA", minWidth: 0, height: "100%" }}>
      {/* Header */}
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="flex flex-row items-center justify-between">
          <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 20, lineHeight: "30px", color: "#000000" }}>{discussion.name}</span>
          <div className="flex flex-row items-center" style={{ gap: 4 }}>
            <button
              onClick={() => { setShowConvSearch(!showConvSearch); if (showConvSearch) setConvSearch(""); }}
              className="hover:bg-neutral-200 rounded-md flex items-center justify-center"
              style={{ width: 32, height: 32, background: showConvSearch ? "#F0F0F0" : "transparent" }}
            >
              <Icon name="search" size={16} color="#666666" />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowMenu((v) => !v)}
                className="hover:bg-neutral-200 rounded-md flex items-center justify-center"
                style={{ width: 32, height: 32, background: showMenu ? "#F0F0F0" : "transparent" }}
              >
                <Icon name="dots" size={16} color="#666666" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute z-50 flex flex-col" style={{ top: 36, right: 0, minWidth: 210, background: "#FFFFFF", borderRadius: 10, border: "1px solid #F0F0F0", boxShadow: "0px 6px 20px rgba(0,0,0,0.12)", overflow: "hidden" }}>
                    <button
                      onClick={() => { setShowMenu(false); setShowInfo(true); }}
                      className="flex items-center hover:bg-neutral-50 w-full"
                      style={{ padding: "10px 14px", gap: 10 }}
                    >
                      <Icon name="eye" size={14} color="#525252" />
                      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Infos de la discussion</span>
                    </button>
                    <button
                      onClick={() => { setShowMenu(false); setShowAddMembers(true); }}
                      className="flex items-center hover:bg-neutral-50 w-full"
                      style={{ padding: "10px 14px", gap: 10 }}
                    >
                      <Icon name="plus" size={14} color="#525252" />
                      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Ajouter des membres</span>
                    </button>
                    <div style={{ height: 1, background: "#F5F5F5" }} />
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        if (window.confirm("Quitter cette discussion ?")) leaveDiscussion(discId);
                      }}
                      className="flex items-center hover:bg-red-50 w-full"
                      style={{ padding: "10px 14px", gap: 10 }}
                    >
                      <Icon name="delete" size={14} color="#ef4444" />
                      <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#ef4444" }}>Quitter la discussion</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {showConvSearch && (
          <input
            autoFocus
            value={convSearch}
            onChange={(e) => setConvSearch(e.target.value)}
            placeholder="Rechercher dans la conversation..."
            className="outline-none border-none"
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000", background: "#F0F0F0", borderRadius: 8, padding: "8px 12px", marginTop: 4 }}
          />
        )}
        <button
          type="button"
          onClick={() => setShowInfo(true)}
          className="flex flex-row items-center hover:opacity-80"
          style={{ gap: 20, marginTop: 4, background: "none", border: "none", padding: 0, cursor: "pointer" }}
        >
          <AvatarStack members={chatMembers} size={24} />
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, lineHeight: "18px", color: "#A1A1A1" }}>
            {chatMembers.length} membre{chatMembers.length > 1 ? "s" : ""}
          </span>
        </button>
      </div>

      {/* Pinned messages */}
      <PinnedBar
        pinned={pinnedMessages}
        onJump={jumpToMessage}
        onUnpin={(id) => unpinMessage(discId, id)}
      />

      {/* Messages */}
      <div ref={scrollRef} onScroll={onScroll} className="flex flex-col flex-1" style={{ gap: 16, overflowY: "auto", minHeight: 0, padding: "0 10px" }}>
        {isLoading && !messages.length && (
          <div className="flex justify-center" style={{ padding: 12 }}>
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Chargement...</span>
          </div>
        )}
        {groups.map((group, gi) => {
          const day = msgDayKey(group.messages[0]);
          const prevDay = gi > 0 ? msgDayKey(groups[gi - 1].messages[0]) : null;
          return (
            <React.Fragment key={group.messages[0].id}>
              {day && day !== prevDay && <DaySeparator iso={group.messages[0].created_at} />}
              <MessageGroup
                group={group}
                members={members}
                discussion={discussion}
                allMessages={messages}
                readReceipts={readReceipts}
                currentUserId={userId}
                navigateWorkspace={navigateWorkspace}
                onJump={jumpToMessage}
                highlightId={highlightId}
                pinnedIds={pinnedIds}
              />
            </React.Fragment>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Typing indicator */}
      {typingNames.length > 0 && (
        <div className="flex items-center" style={{ padding: "4px 16px", gap: 6 }}>
          <span className="inline-flex items-center" style={{ gap: 3 }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="inline-block rounded-full"
                style={{ width: 6, height: 6, background: "#A1A1A1", animation: `waTyping 1.2s ease-in-out ${i * 0.15}s infinite` }}
              />
            ))}
            <style>{`@keyframes waTyping {0%{opacity:.3;transform:translateY(0)}25%{opacity:1;transform:translateY(-3px)}50%{opacity:.3;transform:translateY(0)}100%{opacity:.3;transform:translateY(0)}}`}</style>
          </span>
          <span style={{ fontSize: 12, color: "#A1A1A1", fontFamily: "Poppins" }}>
            {typingNames.join(", ")} {typingNames.length === 1 ? "écrit" : "écrivent"}...
          </span>
        </div>
      )}

      {/* Composer */}
      <ChatInput discussionId={discId} members={members} orgId={orgId} />

      {showAddMembers && (
        <AddMembersModal
          onClose={() => setShowAddMembers(false)}
          members={members}
          existingMemberIds={discussion.members || []}
          onAdd={async (ids) => { await addMembers(discId, ids); setShowAddMembers(false); }}
        />
      )}

      {showInfo && (
        <ConvInfoPanel
          onClose={() => setShowInfo(false)}
          discussionName={discussion.name}
          members={chatMembers}
          messages={messages}
          onAddMembers={() => { setShowInfo(false); setShowAddMembers(true); }}
        />
      )}
    </div>
  );
}

function ConvInfoPanel({ onClose, discussionName, members, messages, onAddMembers }) {
  const [previewImage, setPreviewImage] = useState(null);
  const images = (messages || []).filter(
    (m) => !m.deleted && m.attachment && (m.attachment.type === "image" || (m.attachment.mime_type || "").startsWith("image/")) && m.attachment.url
  );
  const files = (messages || []).filter(
    (m) => !m.deleted && m.attachment && !(m.attachment.type === "image" || (m.attachment.mime_type || "").startsWith("image/"))
  );
  const links = [];
  const seen = new Set();
  const urlRe = /(https?:\/\/[^\s]+)/g;
  (messages || []).forEach((m) => {
    if (m.deleted || !m.text) return;
    const matches = m.text.match(urlRe);
    if (matches) matches.forEach((u) => { if (!seen.has(u)) { seen.add(u); links.push(u); } });
  });

  const Section = ({ title, count, children }) => (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#A1A1A1", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {title}{count != null ? ` (${count})` : ""}
      </span>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,0.25)" }} onClick={onClose}>
      <div
        className="flex flex-col"
        style={{ width: 360, maxWidth: "90vw", height: "100%", background: "#FFFFFF", borderLeft: "1px solid #DDDDDD", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row items-center justify-between" style={{ padding: "16px 20px", borderBottom: "1px solid #F0F0F0" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#000000" }}>Infos</span>
          <button onClick={onClose} className="hover:bg-neutral-100 rounded-md flex items-center justify-center" style={{ width: 28, height: 28 }}>
            <Icon name="close" size={12} color="#A1A1A1" />
          </button>
        </div>

        <div className="flex flex-col items-center" style={{ padding: "20px", gap: 8, borderBottom: "1px solid #F0F0F0" }}>
          <AvatarStack members={members} size={44} maxVisible={4} />
          <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18, color: "#000000", textAlign: "center" }}>{discussionName}</span>
        </div>

        <div className="flex flex-col" style={{ padding: 20, gap: 24 }}>
          <Section title="Membres" count={members.length}>
            <div className="flex flex-col" style={{ gap: 4 }}>
              {members.map((m) => (
                <div key={m.id} className="flex flex-row items-center" style={{ gap: 10, padding: "4px 0" }}>
                  <MemberAvatar member={m} size={30} />
                  <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000000" }}>{m.name}</span>
                </div>
              ))}
            </div>
            <button
              onClick={onAddMembers}
              className="flex flex-row items-center hover:bg-neutral-50"
              style={{ gap: 10, padding: "6px 0", marginTop: 2 }}
            >
              <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 15, background: "#F5F5F5" }}>
                <Icon name="plus" size={14} color="#6366f1" />
              </span>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#6366f1" }}>Ajouter des membres</span>
            </button>
          </Section>

          <Section title="Médias" count={images.length}>
            {images.length ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                {images.map((m) => (
                  <div key={m.id} onClick={() => setPreviewImage(m.attachment)} style={{ display: "block", cursor: "pointer" }} title="Agrandir">
                    <img src={m.attachment.url} alt={m.attachment.name || "image"} style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", borderRadius: 8 }} />
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Aucun média</span>
            )}
          </Section>

          <Section title="Fichiers" count={files.length}>
            {files.length ? (
              files.map((m) => (
                <a key={m.id} href={m.attachment.url} target="_blank" rel="noreferrer" className="flex flex-row items-center hover:bg-neutral-50" style={{ gap: 10, padding: "6px 4px", borderRadius: 8, textDecoration: "none" }}>
                  <Icon name="file" size={16} color="#525252" />
                  <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.attachment.name || "Fichier"}</span>
                </a>
              ))
            ) : (
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Aucun fichier</span>
            )}
          </Section>

          <Section title="Liens" count={links.length}>
            {links.length ? (
              links.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" style={{ fontFamily: "Poppins", fontSize: 13, color: "#3DA7FF", wordBreak: "break-all", padding: "2px 0" }}>{u}</a>
              ))
            ) : (
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Aucun lien</span>
            )}
          </Section>
        </div>
      </div>
      {previewImage && createPortal(
        <DocumentPreviewModal
          doc={{ name: previewImage.name || "image.png" }}
          url={previewImage.base64Data || previewImage.url || ""}
          onClose={() => setPreviewImage(null)}
          onDownload={() => handleDownload(previewImage.url, previewImage.base64Data, previewImage.name || "image.png")}
        />,
        document.body
      )}
    </div>
  );
}

function AddMembersModal({ onClose, members, existingMemberIds, onAdd }) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const candidates = (members || [])
    .filter((m) => !(existingMemberIds || []).includes(m.id))
    .map((u) => getMember([u], u.id))
    .filter((m) => m && (!search || m.name.toLowerCase().includes(search.toLowerCase())));

  const submit = async () => {
    if (!selected.length) return;
    setBusy(true);
    try { await onAdd(selected); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <div className="flex flex-col" style={{ width: 420, maxWidth: "90vw", background: "#FFFFFF", borderRadius: 16, overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-row items-center justify-between" style={{ padding: "16px 20px", borderBottom: "1px solid #F0F0F0" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#000000" }}>Ajouter des membres</span>
          <button onClick={onClose} className="hover:bg-neutral-100 rounded-md flex items-center justify-center" style={{ width: 28, height: 28 }}>
            <Icon name="close" size={12} color="#A1A1A1" />
          </button>
        </div>
        <div className="flex flex-col" style={{ padding: 20, gap: 12 }}>
          <div className="flex flex-row items-center" style={{ background: "#F5F5F5", borderRadius: 10, padding: "8px 12px", gap: 8 }}>
            <Icon name="search" size={15} color="#A1A1A1" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Chercher .."
              className="outline-none border-none flex-1"
              style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000", background: "transparent" }}
            />
          </div>
          <div className="flex flex-col" style={{ maxHeight: 260, overflowY: "auto" }}>
            {candidates.map((m) => {
              const checked = selected.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => setSelected((prev) => (checked ? prev.filter((x) => x !== m.id) : [...prev, m.id]))}
                  className="flex flex-row items-center justify-between hover:bg-neutral-50"
                  style={{ padding: "8px 6px", borderRadius: 8 }}
                >
                  <div className="flex flex-row items-center" style={{ gap: 10 }}>
                    <MemberAvatar member={m} size={28} />
                    <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000000" }}>{m.name}</span>
                  </div>
                  <span className="flex items-center justify-center" style={{ width: 18, height: 18, borderRadius: 5, border: checked ? "none" : "1.5px solid #D4D4D4", background: checked ? "#6366f1" : "transparent" }}>
                    {checked && <Icon name="check" size={11} color="#FFFFFF" />}
                  </span>
                </button>
              );
            })}
            {!candidates.length && (
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1", padding: 8 }}>Tout le monde est déjà membre.</span>
            )}
          </div>
        </div>
        <div className="flex flex-row justify-end" style={{ padding: "12px 20px", gap: 10, borderTop: "1px solid #F0F0F0" }}>
          <button onClick={onClose} className="hover:bg-neutral-100" style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#525252", padding: "9px 16px", borderRadius: 10 }}>Annuler</button>
          <button
            onClick={submit}
            disabled={busy || !selected.length}
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#FFFFFF", background: !selected.length ? "#C7C7C7" : "#000000", padding: "9px 18px", borderRadius: 10 }}
          >
            Ajouter{selected.length ? ` (${selected.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
